"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';
import { Search, MoreHorizontal, Edit, Trash2, Plus, Shield, Loader2 } from "lucide-react";

interface Policy {
  id: number;
  name: string;
  description: string | null;
  config: any;
  createdAt: string;
}

const defaultConfig = {
  allow_file_transfer: true,
  allow_chat: true,
  allow_clipboard: true,
  require_password: true,
  max_session_time: 0,
  allowed_networks: [],
  blocked_networks: [],
};

export function PoliciesPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [formData, setFormData] = useState({ name: "", description: "", config: defaultConfig });
  const [activeTab, setActiveTab] = useState("general");

  const { data: policies, isLoading } = useQuery({
    queryKey: ["policies"],
    queryFn: () => api.getPolicies(token!),
    enabled: !!token,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; config: any }) => api.createPolicy(data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      setCreateDialogOpen(false);
      setFormData({ name: "", description: "", config: defaultConfig });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name: string; description?: string; config: any } }) => api.updatePolicy(id, data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
      setEditDialogOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deletePolicy(id, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["policies"] });
    },
  });

  const handleEdit = (policy: Policy) => {
    setSelectedPolicy(policy);
    setFormData({ name: policy.name, description: policy.description || "", config: policy.config || defaultConfig });
    setEditDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPolicy) {
      updateMutation.mutate({ id: selectedPolicy.id, data: { name: formData.name, description: formData.description, config: formData.config } });
    } else {
      createMutation.mutate({ name: formData.name, description: formData.description, config: formData.config });
    }
  };

  const handleConfigChange = (key: string, value: any) => {
    setFormData({ ...formData, config: { ...formData.config, [key]: value } });
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-4"><Card><CardContent className="pt-6"><div className="h-4 w-3/4 animate-pulse rounded bg-muted" /></CardContent></Card></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Access Policies</h1>
          <p className="text-muted-foreground">Define access control policies for device groups</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />New Policy</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Create Policy</DialogTitle><DialogDescription>Define a new access control policy</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="general" onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="permissions">Permissions</TabsTrigger>
                  <TabsTrigger value="network">Network</TabsTrigger>
                </TabsList>
                <TabsContent value="general" className="py-4">
                  <div className="grid gap-4">
                    <div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
                    <div className="space-y-2"><Label htmlFor="description">Description</Label><Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
                  </div>
                </TabsContent>
                <TabsContent value="permissions" className="py-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div><Label>Allow File Transfer</Label><p className="text-sm text-muted-foreground">Enable file transfer between devices</p></div>
                      <input type="checkbox" checked={formData.config.allow_file_transfer} onChange={(e) => handleConfigChange("allow_file_transfer", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div><Label>Allow Chat</Label><p className="text-sm text-muted-foreground">Enable chat between connected devices</p></div>
                      <input type="checkbox" checked={formData.config.allow_chat} onChange={(e) => handleConfigChange("allow_chat", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div><Label>Allow Clipboard</Label><p className="text-sm text-muted-foreground">Enable clipboard sharing</p></div>
                      <input type="checkbox" checked={formData.config.allow_clipboard} onChange={(e) => handleConfigChange("allow_clipboard", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div><Label>Require Password</Label><p className="text-sm text-muted-foreground">Require password for connections</p></div>
                      <input type="checkbox" checked={formData.config.require_password} onChange={(e) => handleConfigChange("require_password", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Session Time (minutes, 0 = unlimited)</Label>
                      <Input type="number" value={formData.config.max_session_time} onChange={(e) => handleConfigChange("max_session_time", parseInt(e.target.value) || 0)} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="network" className="py-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Allowed Networks (CIDR, one per line)</Label>
                      <textarea value={formData.config.allowed_networks?.join("\n") || ""} onChange={(e) => handleConfigChange("allowed_networks", e.target.value.split("\n").filter(Boolean))} className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                      <p className="text-sm text-muted-foreground">Empty = all networks allowed</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Blocked Networks (CIDR, one per line)</Label>
                      <textarea value={formData.config.blocked_networks?.join("\n") || ""} onChange={(e) => handleConfigChange("blocked_networks", e.target.value.split("\n").filter(Boolean))} className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                      <p className="text-sm text-muted-foreground">These networks will be blocked even if in allowed list</p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); setFormData({ name: "", description: "", config: defaultConfig }); }}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : "Create Policy"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Policies</CardTitle>
            <Input placeholder="Search policies..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>File Transfer</TableHead>
                <TableHead>Chat</TableHead>
                <TableHead>Clipboard</TableHead>
                <TableHead>Require Password</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies?.filter((p: any) => p.name.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase())).map((policy: any) => (
                <TableRow key={policy.id}>
                  <TableCell className="font-medium">{policy.name}</TableCell>
                  <TableCell className="text-muted-foreground">{policy.description || "-"}</TableCell>
                  <TableCell><Badge variant={policy.config.allow_file_transfer ? "success" : "secondary"}>{policy.config.allow_file_transfer ? "Yes" : "No"}</Badge></TableCell>
                  <TableCell><Badge variant={policy.config.allow_chat ? "success" : "secondary"}>{policy.config.allow_chat ? "Yes" : "No"}</Badge></TableCell>
                  <TableCell><Badge variant={policy.config.allow_clipboard ? "success" : "secondary"}>{policy.config.allow_clipboard ? "Yes" : "No"}</Badge></TableCell>
                  <TableCell><Badge variant={policy.config.require_password ? "success" : "secondary"}>{policy.config.require_password ? "Yes" : "No"}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{new Date(policy.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(policy)}><Edit className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => deleteMutation.mutate(policy.id)} className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {policies?.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No policies created yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Policy</DialogTitle><DialogDescription>Update policy settings</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="general" onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3"><TabsTrigger value="general">General</TabsTrigger><TabsTrigger value="permissions">Permissions</TabsTrigger><TabsTrigger value="network">Network</TabsTrigger></TabsList>
              <TabsContent value="general" className="py-4">
                <div className="grid gap-4"><div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div><div className="space-y-2"><Label htmlFor="description">Description</Label><Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div></div>
              </TabsContent>
              <TabsContent value="permissions" className="py-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between"><div><Label>Allow File Transfer</Label><p className="text-sm text-muted-foreground">Enable file transfer between devices</p></div><input type="checkbox" checked={formData.config.allow_file_transfer} onChange={(e) => handleConfigChange("allow_file_transfer", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /></div>
                  <div className="flex items-center justify-between"><div><Label>Allow Chat</Label><p className="text-sm text-muted-foreground">Enable chat between connected devices</p></div><input type="checkbox" checked={formData.config.allow_chat} onChange={(e) => handleConfigChange("allow_chat", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /></div>
                  <div className="flex items-center justify-between"><div><Label>Allow Clipboard</Label><p className="text-sm text-muted-foreground">Enable clipboard sharing</p></div><input type="checkbox" checked={formData.config.allow_clipboard} onChange={(e) => handleConfigChange("allow_clipboard", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /></div>
                  <div className="flex items-center justify-between"><div><Label>Require Password</Label><p className="text-sm text-muted-foreground">Require password for connections</p></div><input type="checkbox" checked={formData.config.require_password} onChange={(e) => handleConfigChange("require_password", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" /></div>
                  <div className="space-y-2"><Label>Max Session Time (minutes, 0 = unlimited)</Label><Input type="number" value={formData.config.max_session_time} onChange={(e) => handleConfigChange("max_session_time", parseInt(e.target.value) || 0)} /></div>
                </div>
              </TabsContent>
              <TabsContent value="network" className="py-4">
                <div className="space-y-4">
                  <div className="space-y-2"><Label>Allowed Networks (CIDR, one per line)</Label><textarea value={formData.config.allowed_networks?.join("\n") || ""} onChange={(e) => handleConfigChange("allowed_networks", e.target.value.split("\n").filter(Boolean))} className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /><p className="text-sm text-muted-foreground">Empty = all networks allowed</p></div>
                  <div className="space-y-2"><Label>Blocked Networks (CIDR, one per line)</Label><textarea value={formData.config.blocked_networks?.join("\n") || ""} onChange={(e) => handleConfigChange("blocked_networks", e.target.value.split("\n").filter(Boolean))} className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /><p className="text-sm text-muted-foreground">These networks will be blocked even if in allowed list</p></div>
                </div>
              </TabsContent>
            </Tabs>
            <DialogFooter><Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); setFormData({ name: "", description: "", config: defaultConfig }); }}>Cancel</Button><Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Changes"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}